import { DEV_MODE } from '../../global/env';
import { isNil, isUndefined, notEqual } from '../../utils/unit';
import { vdsEvent } from '../events';
import { Logger } from '../logger';
export class ContextConsumerController {
    constructor(_host, initialValue, _options) {
        this._host = _host;
        this.initialValue = initialValue;
        this._options = _options;
        this._hasConnectedToProvider = false;
        /**
         * Custom `DisposalBin` so there's no infinite dep cycle:
         *
         * Logger -> ContextConsumer -> DisposalBin -> Logger -> ...
         */
        this._stopDisposal = {
            _bin: [],
            add(cb) {
                if (cb)
                    this._bin.push(cb);
            },
            empty() {
                this._bin.forEach((fn) => fn());
                this._bin = [];
            }
        };
        this.consumerId = Symbol('Vidstack.consumerId');
        /* c8 ignore start */
        if (DEV_MODE && _options.name) {
            this._logger = new Logger(_host, {
                name: `🧵 ${String(this.name)}`,
                owner: this
            });
        }
        /* c8 ignore stop */
        this._value = this._transformValue(initialValue);
        if (_host instanceof Element)
            this.setRef(_host);
        _host.addController(this);
    }
    get id() {
        return this._options.id;
    }
    get name() {
        return this._options.name;
    }
    get value() {
        return this._value;
    }
    /**
     * Whether the consumer is currently connected to a provider.
     */
    get isConnected() {
        return this._hasConnectedToProvider;
    }
    hostConnected() {
        this.start();
    }
    hostDisconnected() {
        this._handleContextUpdate(this.initialValue);
        this.stop();
    }
    /**
     * Set a reference to a DOM element that this controller will use to connect to a provider
     * by dispatching a connect event from it. The reference element's position in the DOM will
     * dictate which provider it connects to, since it'll connect to the first parent provider
     * that provides the current context.
     */
    setRef(newRef) {
        this._ref = newRef;
    }
    /**
     * Start consuming context.
     */
    start() {
        if (this._hasConnectedToProvider || isNil(this._ref))
            return;
        /* c8 ignore start */
        if (DEV_MODE && this.name) {
            this._logger.debug('attempting to connect...');
        }
        /* c8 ignore stop */
        this._ref.dispatchEvent(vdsEvent('vds-context-consumer-connect', {
            bubbles: true,
            composed: true,
            detail: {
                id: this.id,
                name: this.name,
                consumerId: this.consumerId,
                onConnect: this._handleContextConnect.bind(this),
                onUpdate: this._handleContextUpdate.bind(this),
                onDisconnect: this._handleContextDisconnect.bind(this),
                reconnect: this.reconnect.bind(this)
            }
        }));
    }
    /**
     * Stop consuming context.
     */
    stop() {
        var _a, _b;
        if (!this._hasConnectedToProvider)
            return;
        this._stopDisposal.empty();
        (_b = (_a = this._options).onDisconnect) === null || _b === void 0 ? void 0 : _b.call(_a);
        this._hasConnectedToProvider = false;
        /* c8 ignore start */
        if (DEV_MODE && this.name) {
            this._logger.debug('stopped');
        }
        /* c8 ignore stop */
    }
    /**
     * Stop current connection to provider and attempts to reconnect.
     */
    reconnect() {
        /* c8 ignore start */
        if (DEV_MODE && this.name) {
            this._logger.debug('reconnecting');
        }
        /* c8 ignore stop */
        this.stop();
        this.start();
    }
    _handleContextConnect() {
        var _a, _b, _c, _d;
        this._hasConnectedToProvider = true;
        (_b = (_a = this._options).onConnect) === null || _b === void 0 ? void 0 : _b.call(_a);
        (_d = (_c = this._options).onUpdate) === null || _d === void 0 ? void 0 : _d.call(_c, this._value);
        /* c8 ignore start */
        if (DEV_MODE && this.name) {
            this._logger.debug('connected');
        }
        /* c8 ignore stop */
    }
    _handleContextUpdate(newValue) {
        var _a, _b;
        const transformedValue = this._transformValue(newValue);
        if (notEqual(transformedValue, this._value)) {
            this._value = transformedValue;
            (_b = (_a = this._options).onUpdate) === null || _b === void 0 ? void 0 : _b.call(_a, transformedValue);
            if (this._options.shouldRequestUpdate) {
                this._host.requestUpdate();
            }
            /* c8 ignore start */
            if (DEV_MODE && this.name) {
                this._logger.debug('updated to', newValue);
            }
            /* c8 ignore stop */
        }
    }
    _handleContextDisconnect(callback) {
        this._stopDisposal.add(callback);
    }
    _transformValue(value) {
        return !isUndefined(this._options.transform)
            ? this._options.transform(value)
            : value;
    }
}
